import { supabase } from '@/lib/supabase/client';

export interface ClientTestimonial {
  id: string;
  name: string;
  logoUrl: string;
  testimonial: string;
  website: string | null;
  city: string | null;
  state: string | null;
  country: string;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTestimonialData {
  name: string;
  logoUrl: string;
  testimonial: string;
  website?: string;
  city?: string;
  state?: string;
  country?: string;
}

/**
 * Upload logo file to Supabase Storage
 * @param file - The image file to upload (JPG or PNG)
 * @returns The public URL of the uploaded image or null if failed
 */
export async function uploadLogoToStorage(file: File): Promise<string | null> {
  try {
    // Validate file type
    if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
      console.error('Invalid file type. Only JPG and PNG are allowed.');
      return null;
    }

    // Generate unique filename with timestamp
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

    // Upload to Supabase Storage
    const { data, error } = await supabase
      .storage
      .from('clientes logotipo')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Error uploading logo:', error);
      return null;
    }

    // Get public URL
    const { data: { publicUrl } } = supabase
      .storage
      .from('clientes logotipo')
      .getPublicUrl(data.path);

    return publicUrl;
  } catch (error) {
    console.error('Error in uploadLogoToStorage:', error);
    return null;
  }
}

export async function getAllTestimonials(): Promise<ClientTestimonial[]> {
  try {
    const { data, error } = await supabase
      .from('client_testimonials')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) throw error;

    return (data || []).map(row => ({
      id: row.id,
      name: row.name,
      logoUrl: row.logo_url,
      testimonial: row.testimonial,
      website: row.website,
      city: row.city,
      state: row.state,
      country: row.country,
      isActive: row.is_active,
      displayOrder: row.display_order,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    }));
  } catch (error) {
    console.error('Error fetching testimonials:', error);
    return [];
  }
}

export async function getActiveTestimonials(): Promise<ClientTestimonial[]> {
  try {
    const { data, error } = await supabase
      .from('client_testimonials')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) throw error;

    return (data || []).map(row => ({
      id: row.id,
      name: row.name,
      logoUrl: row.logo_url,
      testimonial: row.testimonial,
      website: row.website,
      city: row.city,
      state: row.state,
      country: row.country,
      isActive: row.is_active,
      displayOrder: row.display_order,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    }));
  } catch (error) {
    console.error('Error fetching active testimonials:', error);
    return [];
  }
}

export async function createTestimonial(data: CreateTestimonialData): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('client_testimonials')
      .insert([{
        name: data.name,
        logo_url: data.logoUrl,
        testimonial: data.testimonial,
        website: data.website || null,
        city: data.city || null,
        state: data.state || null,
        country: data.country || 'Brasil'
      }]);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error creating testimonial:', error);
    return false;
  }
}

export async function deleteTestimonial(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('client_testimonials')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting testimonial:', error);
    return false;
  }
}

export async function toggleTestimonialActive(id: string, isActive: boolean): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('client_testimonials')
      .update({ is_active: isActive })
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error toggling testimonial active status:', error);
    return false;
  }
}

export interface UpdateTestimonialData {
  name?: string;
  logoUrl?: string;
  testimonial?: string;
  website?: string;
  city?: string;
  state?: string;
  country?: string;
  isActive?: boolean;
}

export async function updateTestimonial(id: string, data: UpdateTestimonialData): Promise<boolean> {
  try {
    const updateData: any = {};
    if (data.name !== undefined) updateData.name = data.name;
    if (data.logoUrl !== undefined) updateData.logo_url = data.logoUrl;
    if (data.testimonial !== undefined) updateData.testimonial = data.testimonial;
    if (data.website !== undefined) updateData.website = data.website || null;
    if (data.city !== undefined) updateData.city = data.city || null;
    if (data.state !== undefined) updateData.state = data.state || null;
    if (data.country !== undefined) updateData.country = data.country;
    if (data.isActive !== undefined) updateData.is_active = data.isActive;

    const { error } = await supabase
      .from('client_testimonials')
      .update(updateData)
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating testimonial:', error);
    return false;
  }
}

export async function reorderTestimonials(testimonials: { id: string; displayOrder: number }[]): Promise<boolean> {
  try {
    const updates = testimonials.map(({ id, displayOrder }) =>
      supabase
        .from('client_testimonials')
        .update({ display_order: displayOrder })
        .eq('id', id)
    );

    await Promise.all(updates);
    return true;
  } catch (error) {
    console.error('Error reordering testimonials:', error);
    return false;
  }
}